import { useEffect } from 'react';
import type { RealtimeChannel } from '@supabase/supabase-js';
import { supabase, isAuthConfigured } from '../lib/supabase';
import { useAuthStore } from '../store/useAuthStore';
import { useAppStore } from '../store/useAppStore';

/**
 * Subscribes to org-scoped Supabase Realtime channels for DB-level change events.
 * Complements SSE (server-push for computed events) by delivering direct DB
 * insert/update/delete notifications for tenant-owned tables.
 *
 * No-op in bootstrap mode (isAuthConfigured = false) or when not yet onboarded.
 * Cleans up WebSocket channels on unmount or org change.
 *
 * Also performs an initial data load whenever the org context is established
 * (i.e. on login or user switch) so user-specific data is always fresh.
 */
export function useOrgRealtime() {
  const session = useAuthStore((s) => s.session);
  const organizationId = useAuthStore((s) => s.organizationId);
  const {
    fetchAlerts,
    fetchRecommendations,
    fetchBankroll,
    fetchWatchlist,
    fetchBets,
    resetUserData,
    setRealtimeStatus,
    selectedDate,
  } = useAppStore();

  // Clear all user-specific data immediately when the session is lost so
  // no stale values are visible while a new user is logging in.
  useEffect(() => {
    if (!isAuthConfigured) return;
    if (!session) {
      resetUserData();
    }
  }, [session, resetUserData]);

  // Refresh all user-specific data whenever the org context is (re-)established.
  // This fires on initial login and on user switch, ensuring the new user's data
  // is loaded regardless of whether SSEProvider remounts.
  useEffect(() => {
    if (!isAuthConfigured || !session || !organizationId) return;
    fetchBankroll();
    fetchAlerts();
    fetchBets();
    fetchWatchlist();
  }, [session, organizationId, fetchBankroll, fetchAlerts, fetchBets, fetchWatchlist]);

  useEffect(() => {
    if (!isAuthConfigured || !session || !organizationId) return;

    const orgFilter = `organization_id=eq.${organizationId}`;

    const channel: RealtimeChannel = supabase
      .channel(`org-realtime:${organizationId}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'alerts', filter: orgFilter },
        () => fetchAlerts(),
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'recommendations', filter: orgFilter },
        () => fetchRecommendations(selectedDate),
      )
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'bankroll_settings', filter: orgFilter },
        () => fetchBankroll(),
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'watchlist', filter: orgFilter },
        () => fetchWatchlist(),
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'bets', filter: orgFilter },
        () => { fetchBets(); fetchBankroll(); },
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') setRealtimeStatus('connected');
        else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') setRealtimeStatus('disconnected');
      });

    return () => {
      supabase.removeChannel(channel);
    };
  }, [session, organizationId, fetchAlerts, fetchRecommendations, fetchBankroll, fetchWatchlist, fetchBets, setRealtimeStatus, selectedDate]);
}
